import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useGodAuth } from '../../context/GodAuthContext';

const NAV_ITEMS = [
  { path: '/god/dashboard',   icon: '⊞', label: 'Dashboard'   },
  { path: '/god/companies',   icon: '🏢', label: 'Companies'   },
  { path: '/god/offboarding', icon: '📦', label: 'Offboarding' },
  { path: '/god/audit-log',   icon: '📋', label: 'Audit Log'   },
];

// Gold/amber accent colour
const GOLD = '#B7791F';
const GOLD_LIGHT = '#FEF3C7';
const GOLD_BORDER = '#F6AD55';

function isActive(itemPath, currentPath) {
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

export default function GodShell() {
  const { godUser, logout } = useGodAuth();
  const navigate            = useNavigate();
  const location            = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/god/login');
  };

  return (
    <div style={S.shell}>
      {/* Sidebar */}
      <aside style={S.sidebar}>
        {/* Logo */}
        <div style={S.logoArea}>
          <div style={S.logoMark}>K</div>
          <div>
            <p style={S.logoText}>Tracern</p>
            <p style={S.godBadge}>⚡ GOD MODE</p>
          </div>
        </div>

        {/* Nav */}
        <nav style={S.nav}>
          {NAV_ITEMS.map(item => {
            const active = isActive(item.path, location.pathname);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  ...S.navLink,
                  background: active ? GOLD_LIGHT : 'transparent',
                  color:      active ? GOLD       : '#6B7280',
                  fontWeight: active ? 600         : 400,
                  borderLeft: `3px solid ${active ? GOLD_BORDER : 'transparent'}`,
                }}
              >
                <span style={{ fontSize: '16px' }}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={S.footer}>
          <div style={S.userRow}>
            <div style={S.avatar}>{godUser?.username?.[0]?.toUpperCase() || 'G'}</div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 500, color: '#1F2937' }}>{godUser?.username}</p>
              <p style={{ fontSize: '11px', color: '#9CA3AF' }}>{godUser?.role}</p>
            </div>
          </div>
          <button onClick={handleLogout} style={S.logoutBtn}>Sign out</button>
          <p style={{ fontSize: '10px', color: '#9CA3AF', textAlign: 'center', marginTop: '10px', lineHeight: 1.5 }}>
            © {new Date().getFullYear()} Mahashunya Innovation Labs
          </p>
        </div>
      </aside>

      {/* Main */}
      <main style={S.main}>
        {/* Top bar */}
        <div style={S.topbar}>
          <div style={S.topbarLeft}>
            <span style={S.liveBadge}>● LIVE</span>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
              Platform Control Centre — all actions permanently logged
            </span>
          </div>
          <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
            {new Date().toLocaleDateString('en-IN', {
              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
            })}
          </span>
        </div>

        <div style={{ padding: '24px', flex: 1 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

const S = {
  shell:   { display: 'flex', minHeight: '100vh', background: '#F9FAFB' },
  sidebar: {
    width: '224px', background: '#FFFFFF',
    borderRight: '1px solid #E5E7EB',
    display: 'flex', flexDirection: 'column',
    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
    boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
  },
  logoArea: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '20px 16px 16px',
    borderBottom: '1px solid #E5E7EB',
  },
  logoMark: {
    width: '36px', height: '36px', borderRadius: '10px',
    background: `linear-gradient(135deg, ${GOLD} 0%, #D97706 100%)`,
    color: '#fff', fontSize: '18px', fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, boxShadow: `0 2px 8px ${GOLD}44`,
  },
  logoText: { fontSize: '16px', fontWeight: 700, color: '#1F2937', lineHeight: 1.2 },
  godBadge: { fontSize: '9px', fontWeight: 800, color: GOLD, letterSpacing: '0.08em' },
  nav:      { flex: 1, padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navLink: {
    display: 'flex', alignItems: 'center', gap: '10px',
    width: '100%', padding: '9px 12px',
    borderRadius: '0 8px 8px 0',
    border: 'none', fontSize: '14px', cursor: 'pointer', textAlign: 'left',
    transition: 'all 0.15s',
  },
  footer:   { borderTop: '1px solid #E5E7EB', padding: '12px 16px' },
  userRow:  { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  avatar:   {
    width: '30px', height: '30px', borderRadius: '50%',
    background: GOLD_LIGHT, border: `1.5px solid ${GOLD_BORDER}`,
    color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: 700, flexShrink: 0,
  },
  logoutBtn: {
    width: '100%', padding: '7px',
    borderRadius: '6px',
    background: '#F9FAFB', color: '#6B7280',
    fontSize: '12px', border: '1px solid #E5E7EB', cursor: 'pointer',
  },
  main: {
    flex: 1, marginLeft: '224px',
    display: 'flex', flexDirection: 'column', minHeight: '100vh',
    background: '#F9FAFB',
  },
  topbar: {
    height: '48px', background: '#FFFFFF',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', position: 'sticky', top: 0, zIndex: 50,
  },
  topbarLeft:  { display: 'flex', alignItems: 'center', gap: '12px' },
  liveBadge:   { fontSize: '11px', color: '#16A34A', fontWeight: 700, letterSpacing: '0.05em' },
};
